import { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import './careerSessionSlots.css';
import { CareerGuidanceService } from '../../../../services/careerGuidanceService';
import SigniteButton from '../../../ui-elements/signite-button/signiteButton';
import Loader from 'react-spinner-loader';

const formatDateTimeLocal = (value) => {
    if (!value) return '';
    const date = new Date(Number(value));
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const CareerSessionSlots = ({
    show,
    handleClose,
    session
}) => {

    const [formData, setFormData] = useState({
        startTime: '',
        endTime: '',
        availableSeats: ''
    });

    const [editMode, setEditMode] = useState(false);
    const [selectedSlotId, setSelectedSlotId] = useState(null);
    const [showSlotFormModal, setShowSlotFormModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    const fetchSlots = async () => {
        if (!session?.id) {
            return;
        }

        setLoadingSlots(true);

        try {

            const response = await CareerGuidanceService.getSlots(session.id);
            const data = response.data;
            setSlots(
                Array.isArray(data)
                    ? data
                    : data?.slots || []
            );

        } catch (error) {
            console.error('Error fetching slots:', error);
            setSlots([]);

        } finally {
            setLoadingSlots(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchSlots();
        }

    }, [show, session]);

    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

    };

    const resetForm = () => {

        setFormData({
            startTime: '',
            endTime: '',
            availableSeats: ''
        });

        setEditMode(false);
        setSelectedSlotId(null);

    };

    const handleSave = async () => {

        try {

            const request = {
                startTime: new Date(
                    formData.startTime
                ).getTime(),

                endTime: new Date(
                    formData.endTime
                ).getTime(),

                availableSeats: Number(
                    formData.availableSeats
                )
            };

            if (editMode) {

                await CareerGuidanceService.updateSlot(
                    selectedSlotId,
                    request
                );

            } else {

                await CareerGuidanceService.addSlot(
                    session.id,
                    request
                );

            }

            setSuccessMessage(
                editMode
                    ? 'Slot updated successfully.'
                    : 'Slot added successfully.'
            );

            setShowSuccessModal(true);

            setShowSlotFormModal(false);

            resetForm();

            fetchSlots();

        } catch (error) {

            console.error(error);

            alert('Unable to save slot.');

        }

    };

    const handleEdit = (slot) => {

        setEditMode(true);

        setSelectedSlotId(slot.id);

        setFormData({
            startTime: formatDateTimeLocal(slot.startTime),
            endTime: formatDateTimeLocal(slot.endTime),
            availableSeats: slot.availableSeats
        });

        setShowSlotFormModal(true);

    };

    const handleNew = () => {

        resetForm();

        setShowSlotFormModal(true);

    };

    const handleCloseSlotForm = () => {

        setShowSlotFormModal(false);

        resetForm();

    };

    return (
        <>

            <Modal
                show={show}
                onHide={handleClose}
                size="xl"
                centered
            >

                <Modal.Header closeButton>

                    <Modal.Title>
                        Manage Career Session Slots
                    </Modal.Title>

                </Modal.Header>

                <Modal.Body className="career-slots-modal-body">

                    {loadingSlots ? (

                        <div className="slots-loading-overlay">
                            <div className="slots-orange-loader"></div>
                        </div>

                    ) : (

                        <>
                            <div className="slot-header">

                                <div>
                                    <div className="slot-session-title">
                                        {session?.headline}
                                    </div>

                                    <div className="slot-session-description">
                                        {session?.descriptionText}
                                    </div>
                                </div>

                                <SigniteButton
                                    type="button"
                                    variant="primary"
                                    className="slot-primary-btn"
                                    onClick={(e) => {
                                        handleNew();
                                        e.currentTarget.blur();
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faPlusCircle}
                                        size="sm"
                                        className="me-2"
                                    />

                                    <span>Create Slot</span>
                                </SigniteButton>

                            </div>

                            <Table
                                bordered
                                hover
                                responsive
                                className="slot-table"
                            >
                                <thead>
                                    <tr>
                                        <th>S.No</th>
                                        <th>Session Start Time</th>
                                        <th>Session End Time</th>
                                        <th>Available Seats</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>

                                <tbody>

                                    {slots.length === 0 ? (

                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="slot-empty"
                                            >
                                                No Slots Available
                                            </td>
                                        </tr>

                                    ) : (

                                        slots.map((slot, index) => (

                                            <tr key={slot.id}>

                                                <td>{index + 1}</td>

                                                <td>
                                                    {new Date(
                                                        slot.startTime
                                                    ).toLocaleString()}
                                                </td>

                                                <td>
                                                    {new Date(
                                                        slot.endTime
                                                    ).toLocaleString()}
                                                </td>

                                                <td>
                                                    {slot.availableSeats}
                                                </td>

                                                <td>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        className="slot-edit-btn"
                                                        onClick={(e) => {
                                                            handleEdit(slot);
                                                            e.currentTarget.blur();
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faEdit}
                                                            className="me-1"
                                                        />
                                                        Edit
                                                    </Button>
                                                </td>

                                            </tr>

                                        ))

                                    )}

                                </tbody>
                            </Table>
                        </>

                    )}

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        className="slot-cancel-btn"
                        onClick={handleClose}
                    >
                        Close
                    </Button>

                </Modal.Footer>

            </Modal>

            <Modal
                show={showSlotFormModal}
                onHide={handleCloseSlotForm}
                centered
                backdrop="static"
                className="slot-form-modal"
            >

                <Modal.Header closeButton>

                    <Modal.Title>

                        {editMode
                            ? 'Edit Career Session Slot'
                            : 'Create Career Session Slot'}

                    </Modal.Title>

                </Modal.Header>

                <Modal.Body>

                    <Form>

                        <Row>

                            <Col md={12}>

                                <Form.Group className="mb-3">

                                    <Form.Label>
                                        Session Start Time
                                    </Form.Label>

                                    <Form.Control
                                        type="datetime-local"
                                        name="startTime"
                                        value={formData.startTime}
                                        onChange={handleChange}
                                    />

                                </Form.Group>

                            </Col>

                            <Col md={12}>

                                <Form.Group className="mb-3">

                                    <Form.Label>
                                        Session End Time
                                    </Form.Label>

                                    <Form.Control
                                        type="datetime-local"
                                        name="endTime"
                                        value={formData.endTime}
                                        onChange={handleChange}
                                    />

                                </Form.Group>

                            </Col>

                            <Col md={12}>

                                <Form.Group className="mb-3">

                                    <Form.Label>
                                        Available Seats
                                    </Form.Label>

                                    <Form.Control
                                        type="number"
                                        name="availableSeats"
                                        value={formData.availableSeats}
                                        onChange={handleChange}
                                        min="1"
                                    />

                                </Form.Group>

                            </Col>

                        </Row>

                    </Form>

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        className="slot-cancel-btn"
                        onClick={handleCloseSlotForm}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="primary"
                        className="slot-primary-btn"
                        onClick={handleSave}
                    >

                        {editMode
                            ? 'Update Slot'
                            : 'Add Slot'}

                    </Button>

                </Modal.Footer>

            </Modal>

            <Modal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                centered
                className="slot-success-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Success
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="text-center">
                    <div className="slot-success-icon">
                        ✓
                    </div>

                    <div className="slot-success-message">
                        {successMessage}
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        type="button"
                        className="slot-primary-btn"
                        onClick={() => setShowSuccessModal(false)}
                    >
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default CareerSessionSlots;